import { useEffect, useRef, useState } from 'react';
import {
    Paper, Stack, Title, Text, Group, Textarea, Button, ScrollArea,
    Avatar, Badge, Tooltip, Loader, Alert, Indicator, Modal,
} from '@mantine/core';
import {
    IconSend, IconLogout, IconRobot, IconUser, IconAlertCircle, IconTool,
} from '@tabler/icons-react';
import ServiceRequestForm from './ServiceRequestForm';
import { authedFetch, cfg } from './session';

export default function ChatScreen({ session, onLogout, embedded = false }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [agent, setAgent] = useState(null);
    const [actionContext, setActionContext] = useState(null); // for the service-request modal
    const scrollRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const [hist, ag] = await Promise.all([
                    fetch(`${cfg.apiUrl}/support/session/${session.session_token}`).then((r) => r.ok ? r.json() : null),
                    fetch(`${cfg.apiUrl}/support/active-agent/${session.session_token}`).then((r) => r.ok ? r.json() : null),
                ]);
                if (hist) setMessages(hist.messages || []);
                if (ag?.agent) setAgent(ag.agent);
            } catch (err) {
                console.warn(err);
            } finally {
                setLoading(false);
            }
        })();
    }, [session.session_token]);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
        }
    }, [messages, sending]);

    async function send(e) {
        e?.preventDefault();
        const text = input.trim();
        if (!text || sending) return;
        setSending(true);
        setError(null);

        const tempUserMsg = { id: `temp-${Date.now()}`, role: 'user', content: text, created_at: new Date().toISOString() };
        setMessages((prev) => [...prev, tempUserMsg]);
        setInput('');

        try {
            const body = await authedFetch('/support/chat', {
                method: 'POST',
                body: { session_token: session.session_token, message: text },
            });
            if (body.agent) setAgent((prev) => ({ ...prev, ...body.agent }));

            setMessages((prev) => [...prev, {
                id: `srv-${Date.now()}`,
                role: 'assistant',
                content: body.reply,
                actions: body.actions || [],
                created_at: new Date().toISOString(),
            }]);
        } catch (err) {
            setError(err.message);
        } finally {
            setSending(false);
        }
    }

    function handleKey(e) {
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            send();
        }
    }

    function triggerAction(action) {
        if (action.type === 'create_service_request') {
            setActionContext({
                title: action.args.title || '',
                ambulance_hint: action.args.ambulance || '',
                urgency: action.args.urgency || 'normal',
            });
        }
    }

    const customerLabel = session.customer
        ? (session.customer.company_name
            || `${session.customer.first_name || ''} ${session.customer.last_name || ''}`.trim()
            || session.customer.email)
        : 'Klant';

    const agentName = agent?.name || (cfg.companyName ? `${cfg.companyName} support` : 'Support');
    const agentRole = agent?.role || 'AI-medewerker';
    const agentEmoji = agent?.avatar_emoji || '';
    const agentColor = agent?.avatar_color || 'red';
    const isOnline = agent ? agent.is_online_now : true;

    const outerStyle = embedded
        ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }
        : {
            display: 'flex', flexDirection: 'column',
            maxWidth: 820, margin: '0 auto', height: '100vh', padding: '16px',
        };

    return (
        <div style={outerStyle}>
            {!embedded && (
                <Paper withBorder p="md" mb="md">
                    <Group justify="space-between">
                        <AgentHeader agent={agent} agentName={agentName} agentRole={agentRole}
                                     agentEmoji={agentEmoji} agentColor={agentColor} isOnline={isOnline} />
                        <Group gap="md">
                            <div style={{ textAlign: 'right' }}>
                                <Text size="xs" c="dimmed">Ingelogd als</Text>
                                <Text size="xs">{customerLabel}</Text>
                            </div>
                            <Tooltip label="Uitloggen">
                                <Button variant="subtle" size="xs" color="gray"
                                        leftSection={<IconLogout size={14} />} onClick={onLogout}>
                                    Uitloggen
                                </Button>
                            </Tooltip>
                        </Group>
                    </Group>
                    {agent && !isOnline && agent.next_window && (
                        <Text size="xs" c="dimmed" mt={6}>
                            Buiten kantooruren — een collega antwoordt {agent.next_window.day_label} vanaf {agent.next_window.time}.
                        </Text>
                    )}
                </Paper>
            )}

            <Paper withBorder={!embedded} style={{
                flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0,
                border: embedded ? 'none' : undefined,
            }}>
                <ScrollArea viewportRef={scrollRef} style={{ flex: 1 }} p="md">
                    {loading ? (
                        <Group justify="center" mt="xl"><Loader size="sm" /></Group>
                    ) : messages.length === 0 ? (
                        <Stack align="center" mt={embedded ? 24 : 48}>
                            <Avatar color={agentColor} size="lg" radius="xl">
                                {agentEmoji
                                    ? <span style={{ fontSize: 28 }}>{agentEmoji}</span>
                                    : <IconRobot size={28} />}
                            </Avatar>
                            <Title order={5}>
                                Hallo! Ik ben {agent?.name?.split(' ')[0] || agentName}
                            </Title>
                            <Text size="sm" c="dimmed" ta="center" maw={400}>
                                Ik help u graag met vragen over uw voertuigen, garantie en service.
                            </Text>
                            {agent?.bio && (
                                <Text size="xs" c="dimmed" ta="center" maw={400}>{agent.bio}</Text>
                            )}
                            <Text size="xs" c="dimmed" ta="center">Voor noodgevallen: bel 112.</Text>
                        </Stack>
                    ) : (
                        <Stack gap="md">
                            {messages.map((m) => (
                                <Message
                                    key={m.id}
                                    message={m}
                                    agent={agent}
                                    customerLabel={customerLabel}
                                    onAction={triggerAction}
                                />
                            ))}
                            {sending && (
                                <Group gap="xs">
                                    <Avatar color={agentColor} radius="xl" size="sm">
                                        {agentEmoji
                                            ? <span style={{ fontSize: 16 }}>{agentEmoji}</span>
                                            : <IconRobot size={14} />}
                                    </Avatar>
                                    <Loader size="xs" />
                                    <Text size="xs" c="dimmed">
                                        {(agent?.name?.split(' ')[0] || 'Support')} typt...
                                    </Text>
                                </Group>
                            )}
                        </Stack>
                    )}
                </ScrollArea>

                {error && (
                    <Alert color="red" icon={<IconAlertCircle size={16} />} m="md" mt={0} variant="light">
                        {error}
                    </Alert>
                )}

                <form onSubmit={send} style={{ padding: 12, borderTop: '1px solid var(--mantine-color-default-border)' }}>
                    <Group gap="xs" align="flex-end">
                        <Textarea
                            placeholder="Stel uw vraag..."
                            value={input}
                            onChange={(e) => setInput(e.currentTarget.value)}
                            onKeyDown={handleKey}
                            disabled={sending}
                            autosize minRows={1} maxRows={4}
                            style={{ flex: 1 }}
                            autoFocus={!embedded}
                        />
                        <Button type="submit" loading={sending} disabled={!input.trim()}
                                leftSection={<IconSend size={16} />}>
                            {embedded ? '' : 'Verstuur'}
                        </Button>
                    </Group>
                </form>
            </Paper>

            <Modal
                opened={!!actionContext}
                onClose={() => setActionContext(null)}
                size="lg"
                title="Service-aanvraag indienen"
            >
                {actionContext && (
                    <ServiceRequestForm
                        prefilledTitle={actionContext.title}
                        onClose={() => setActionContext(null)}
                        onSubmitted={() => setActionContext(null)}
                    />
                )}
            </Modal>
        </div>
    );
}

function AgentHeader({ agent, agentName, agentRole, agentEmoji, agentColor, isOnline }) {
    return (
        <Group gap="sm">
            <Indicator color={isOnline ? 'green' : 'gray'} offset={4} size={10} withBorder position="bottom-end">
                <Avatar color={agentColor} radius="xl" size="md">
                    {agentEmoji
                        ? <span style={{ fontSize: 22 }}>{agentEmoji}</span>
                        : <IconRobot size={20} />}
                </Avatar>
            </Indicator>
            <div>
                <Title order={4} mb={0}>{agentName}</Title>
                <Group gap={6}>
                    <Text size="xs" c="dimmed">{agentRole}</Text>
                    <Text size="xs" c={isOnline ? 'green' : 'dimmed'}>
                        {isOnline ? '· online' : '· offline'}
                    </Text>
                </Group>
            </div>
        </Group>
    );
}

function Message({ message, agent, customerLabel, onAction }) {
    const isUser = message.role === 'user';
    const agentEmoji = agent?.avatar_emoji || '';
    const agentColor = agent?.avatar_color || 'red';
    const agentFirst = agent?.name?.split(' ')[0] || 'AI';

    return (
        <Stack gap={6}>
            <Group gap="xs" align="flex-start" justify={isUser ? 'flex-end' : 'flex-start'}>
                {!isUser && (
                    <Avatar color={agentColor} radius="xl" size="sm">
                        {agentEmoji
                            ? <span style={{ fontSize: 16 }}>{agentEmoji}</span>
                            : <IconRobot size={14} />}
                    </Avatar>
                )}
                <Paper
                    withBorder={!isUser}
                    bg={isUser ? 'var(--mantine-color-red-1)' : undefined}
                    p="sm"
                    style={{ maxWidth: '75%' }}
                >
                    {!isUser && (
                        <Badge size="xs" variant="light" color={agentColor} mb={4}>{agentFirst}</Badge>
                    )}
                    <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{message.content}</Text>
                </Paper>
                {isUser && (
                    <Avatar color="gray" radius="xl" size="sm" title={customerLabel}>
                        <IconUser size={14} />
                    </Avatar>
                )}
            </Group>
            {!isUser && Array.isArray(message.actions) && message.actions.length > 0 && (
                <Group justify="flex-start" pl={36}>
                    {message.actions.map((a, i) => (
                        <ActionButton key={i} action={a} onClick={() => onAction?.(a)} />
                    ))}
                </Group>
            )}
        </Stack>
    );
}

function ActionButton({ action, onClick }) {
    if (action.type === 'create_service_request') {
        const label = action.args.ambulance
            ? `Service-aanvraag indienen voor ${action.args.ambulance}`
            : 'Service-aanvraag indienen';
        return (
            <Button size="xs" variant="light" leftSection={<IconTool size={14} />} onClick={onClick}>
                {label}
            </Button>
        );
    }
    return null;
}
